import React, { Component, ErrorInfo, ReactNode } from "react";
import { Box, Typography, Button } from "@mui/material";
import { useRouteError, isRouteErrorResponse } from "react-router-dom";

function isChunkLoadError(error: any): boolean {
  return (
    error?.name === "ChunkLoadError" ||
    error?.message?.includes("Loading chunk") ||
    error?.message?.includes("Failed to fetch dynamically imported module") ||
    error?.message?.includes("timeout")
  );
}

interface ErrorFallbackUIProps {
  title: string;
  description: string;
  showRetry?: boolean;
  showHome?: boolean;
}

const ErrorFallbackUI: React.FC<ErrorFallbackUIProps> = ({
  title,
  description,
  showRetry = true,
  showHome = true,
}) => {
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        minHeight: "100vh",
        gap: 2,
        p: 3,
        backgroundColor: "#f5f5f5",
      }}
    >
      <Box
        sx={{
          backgroundColor: "#fff",
          borderRadius: 3,
          boxShadow: 3,
          p: 5,
          maxWidth: 450,
          width: "100%",
          textAlign: "center",
        }}
      >
        <Box
          sx={{
            width: 64,
            height: 64,
            borderRadius: "50%",
            backgroundColor: "#fdecea",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            mx: "auto",
            mb: 3,
          }}
        >
          <Typography sx={{ fontSize: 32 }}>!</Typography>
        </Box>

        <Typography variant="h5" fontWeight="bold" gutterBottom>
          {title}
        </Typography>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
          {description}
        </Typography>

        <Box sx={{ display: "flex", gap: 2, justifyContent: "center" }}>
          {showRetry && (
            <Button
              variant="contained"
              onClick={() => window.location.reload()}
            >
              Try Again
            </Button>
          )}
          {showHome && (
            <Button
              variant="outlined"
              onClick={() =>
                (window.location.href =
                  window.location.origin + window.location.pathname)
              }
            >
              Go to Home
            </Button>
          )}
        </Box>
      </Box>
    </Box>
  );
};

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
    error: null,
  };

  public static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Errors can be logged to an external service here
  }

  public render() {
    if (this.state.hasError) {
      if (isChunkLoadError(this.state.error)) {
        return (
          <ErrorFallbackUI
            title="Connection Issue"
            description="We're having trouble loading this page. This usually happens due to a slow or unstable network connection. Please check your internet and try again."
          />
        );
      }

      return (
        <ErrorFallbackUI
          title="Something Went Wrong"
          description="An unexpected error occurred. Please try refreshing the page. If the problem persists, contact support."
        />
      );
    }

    return this.props.children;
  }
}

export function RouteErrorFallback() {
  const error = useRouteError();

  if (isRouteErrorResponse(error)) {
    if (error.status === 404) {
      return (
        <ErrorFallbackUI
          title="Page Not Found"
          description="The page you're looking for doesn't exist or has been moved."
          showRetry={false}
        />
      );
    }

    return (
      <ErrorFallbackUI
        title="Something Went Wrong"
        description={error.statusText || "An unexpected error occurred."}
      />
    );
  }

  const err = error as Error;

  if (isChunkLoadError(err)) {
    return (
      <ErrorFallbackUI
        title="Connection Issue"
        description="We're having trouble loading this page. This usually happens due to a slow or unstable network connection. Please check your internet and try again."
      />
    );
  }

  return (
    <ErrorFallbackUI
      title="Something Went Wrong"
      description="An unexpected error occurred. Please try refreshing the page. If the problem persists, contact support."
    />
  );
}

export default ErrorBoundary;
